import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";
import { UI, btnSx } from "../components/ActionDrawer";
import DashTable, {
  FilterChips,
  KV,
  PageHead,
  RowCard,
  SearchBox,
  Sub2,
  Tag,
  Toolbar,
} from "../components/DashTable";

const rupee = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
      })
    : "—";

const STATUS_META = {
  not_requested: { label: "NOT REQUESTED", tone: "i" },
  unpaid: { label: "UNPAID", tone: "s" },
  partial: { label: "PARTIALLY PAID", tone: "bl" },
  paid: { label: "FULLY PAID", tone: "g" },
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "not_requested", label: "Not requested" },
  { key: "unpaid", label: "Unpaid" },
  { key: "partial", label: "Partially paid" },
  { key: "paid", label: "Fully paid" },
];

const clinicStatus = (row) => {
  if (!row.linksCount) return "not_requested";
  // Summing already-rounded per-order balances across a clinic can still
  // leave a sub-paisa remainder — anything under a rupee isn't a real
  // outstanding balance, it's float noise.
  if (row.totalOutstanding < 1) return "paid";
  if (row.totalPaid > 0) return "partial";
  return "unpaid";
};

const StatusChip = ({ status }) => (
  <Tag tone={STATUS_META[status]?.tone || "i"}>
    {STATUS_META[status]?.label || status}
  </Tag>
);

const OwnerTag = ({ row }) => (
  <Tag tone={row.ownerIsDealer ? "s" : "i"}>
    {row.ownerIsDealer ? "DEALER" : "INTERNAL"}
  </Tag>
);

const PAGE_SIZE = 15;

const PaymentLinks = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchSummary = async (targetPage = page) => {
    setIsLoading(true);
    try {
      const res = await apiService.fetchClinicPaymentSummary(token, {
        page: targetPage,
        limit: PAGE_SIZE,
        status: statusFilter === "all" ? undefined : statusFilter,
        search: search || undefined,
      });
      setRows(res.data || []);
      setSummary(res.summary || null);
      setPagination(res.pagination || null);
    } catch (error) {
      toast.error("Failed to load payment links");
    } finally {
      setIsLoading(false);
    }
  };

  // Typing resets to page 1 and debounces the actual search request so each
  // keystroke doesn't fire its own network call.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  useEffect(() => {
    fetchSummary(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, search]);

  // Filtering/search now happen server-side (see api.service.js
  // fetchClinicPaymentSummary) — `rows` already reflects the current
  // filter/search, scoped correctly across the whole dataset, not just this page.
  const filteredRows = rows;

  const kpis = [
    { label: "Clinics", value: summary?.clinicsCount ?? 0 },
    { label: "Links created", value: summary?.linksCount ?? 0 },
    { label: "Total paid", value: rupee(summary?.totalPaid ?? 0) },
    {
      label: "Outstanding",
      value: rupee(summary?.totalOutstanding ?? 0),
      alarm: true,
    },
  ];

  const goToClinic = (row) =>
    navigate(`/dashboard/payment-links/${row.clinicId}`, {
      state: { clinicName: row.clinicName },
    });

  const columns = [
    { label: "Clinic" },
    { label: "Owner" },
    { label: "Date" },
    { label: "Status" },
    { label: "Quoted", num: true },
    { label: "Paid", num: true },
    { label: "Outstanding", num: true },
  ];

  const tableRows = filteredRows.map((row) => ({
    id: row.clinicId,
    key: row.clinicId,
    cells: [
      <Box>
        <Box component="span" sx={{ color: UI.ink }}>
          {row.clinicName}
        </Box>
        <Sub2>
          {row.ordersCount} order{row.ordersCount === 1 ? "" : "s"}
        </Sub2>
      </Box>,
      <>
        <Box component="span" sx={{ fontWeight: 500 }}>
          {row.ownerName || "—"}
        </Box>
        <Box sx={{ marginTop: "4px" }}>
          <OwnerTag row={row} />
        </Box>
      </>,
      formatDate(row.lastOrderDate),
      <StatusChip status={clinicStatus(row)} />,
      rupee(row.totalQuoted),
      rupee(row.totalPaid),
      <Box
        component="span"
        sx={{
          fontWeight: 500,
          color: row.totalOutstanding > 0 ? UI.amberInk : UI.ink,
        }}
      >
        {rupee(row.totalOutstanding)}
      </Box>,
    ],
  }));

  const emptyText =
    rows.length === 0
      ? "No clinics with orders yet."
      : "No results match your search.";

  return (
    <Box
      sx={{
        padding: { xs: "18px 16px 50px", md: "24px 28px 60px" },
        maxWidth: 1240,
      }}
    >
      <PageHead
        title="Payment links"
        lede="Every clinic with orders, and how much of their quote is still outstanding."
        kpis={kpis}
      />

      <Toolbar
        right={
          <SearchBox
            value={searchInput}
            placeholder="Search by clinic name"
            onChange={(e) => setSearchInput(e.target.value)}
          />
        }
      >
        <FilterChips
          options={FILTERS.map((f) => ({ value: f.key, label: f.label }))}
          value={statusFilter}
          onChange={setStatusFilter}
        />
      </Toolbar>

      {/* MOBILE: stacked cards */}
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          flexDirection: "column",
          gap: "10px",
          marginBottom: "28px",
        }}
      >
        {filteredRows.length === 0 ? (
          <Box
            sx={{
              padding: "34px 18px",
              textAlign: "center",
              fontSize: 13,
              color: UI.ink3,
              bgcolor: UI.surface,
              border: "1px solid",
              borderColor: UI.line,
              borderRadius: "8px",
            }}
          >
            {emptyText}
          </Box>
        ) : (
          filteredRows.map((row) => (
            <RowCard key={row.clinicId} onClick={() => goToClinic(row)}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "8px",
                  marginBottom: "6px",
                }}
              >
                <Box>
                  <Box sx={{ fontSize: 13, fontWeight: 500, color: UI.ink }}>
                    {row.clinicName}
                  </Box>
                  <Sub2>
                    {row.ordersCount} order{row.ordersCount === 1 ? "" : "s"}
                  </Sub2>
                </Box>
                <StatusChip status={clinicStatus(row)} />
              </Box>
              <KV
                label="Owner"
                value={
                  <Box component="span" sx={{ fontWeight: 500 }}>
                    {row.ownerName || "—"}
                  </Box>
                }
              />
              <KV label="Date" value={formatDate(row.lastOrderDate)} />
              <KV label="Quoted" value={rupee(row.totalQuoted)} />
              <KV label="Paid" value={rupee(row.totalPaid)} />
              <KV
                label="Outstanding"
                value={
                  <Box component="span" sx={{ fontWeight: 500 }}>
                    {rupee(row.totalOutstanding)}
                  </Box>
                }
              />
            </RowCard>
          ))
        )}
      </Box>

      {/* DESKTOP: table */}
      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        <DashTable
          columns={columns}
          rows={tableRows}
          onRowClick={(clinicId) =>
            goToClinic(rows.find((r) => r.clinicId === clinicId))
          }
          emptyText={emptyText}
          foot={`${filteredRows.length} shown · ${pagination?.total ?? rows.length} total`}
        />
      </Box>

      {pagination && pagination.total > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
            marginTop: "10px",
            fontSize: 13,
            color: UI.ink3,
          }}
        >
          <span>
            {(pagination.page - 1) * pagination.limit + 1}–
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
            {pagination.total}
          </span>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Box sx={{ display: "flex", gap: "8px" }}>
              <Box
                component="button"
                type="button"
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={pagination.page <= 1}
                sx={btnSx("ghost", pagination.page <= 1)}
              >
                Previous
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages))}
                disabled={pagination.page >= pagination.totalPages}
                sx={btnSx("ghost", pagination.page >= pagination.totalPages)}
              >
                Next
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default PaymentLinks;
